import { useCurrency } from '../../context/CurrencyContext';
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Shield, ShieldAlert, ShieldCheck, Activity, Clock, Eye,
  AlertTriangle, CheckCircle, Lock, Database, Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../api/client';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', chart: '#dc2626' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', chart: '#ea580c' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', chart: '#d97706' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', chart: '#2563eb' },
};

const TYPE_LABELS = {
  rapid_redemption: 'Rapid Redemptions',
  multi_store_rapid: 'Multi-Store Activity',
  high_value: 'High Value Transaction',
  device_mismatch: 'Device Mismatch',
  replay_attempt: 'Replay Attempt',
};

const LEDGER_COLORS = ['#561D1A', '#EC1A23', '#D4AF37', '#16a34a', '#2563eb', '#7c3aed'];

export default function SecurityDashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/store/admin/security/dashboard');
      setData(res.data);
    } catch {
      toast.error('Failed to load security dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { formatDateTime: formatDate } = useCurrency();

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
      </div>
      <div className="h-72 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  if (!data) return null;
  const { summary, flags_by_type, flags_by_severity, ledger_by_type, daily_flag_trend, redemption_trend, recent_flags } = data;

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="security-kpis">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Open Flags</p>
            <ShieldAlert size={16} className={summary.open_flags > 0 ? 'text-red-500' : 'text-gray-300'} />
          </div>
          <p className={`font-heading text-2xl font-bold ${summary.open_flags > 0 ? 'text-red-600' : 'text-gray-800'}`}>{summary.open_flags}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Resolved</p>
            <ShieldCheck size={16} className="text-emerald-500" />
          </div>
          <p className="font-heading text-2xl font-bold text-emerald-600">{summary.resolved_flags}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Ledger Entries</p>
            <Database size={16} className="text-[#561D1A]" />
          </div>
          <p className="font-heading text-2xl font-bold text-[#561D1A]">{summary.total_ledger_entries}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Rate Limit Hits (1h)</p>
            <Zap size={16} className="text-amber-500" />
          </div>
          <p className="font-heading text-2xl font-bold text-gray-800">{summary.rate_limit_hits_1h}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">OTP Challenges (24h)</p>
            <Lock size={16} className="text-purple-500" />
          </div>
          <p className="font-heading text-2xl font-bold text-gray-800">{summary.otp_challenges_24h}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">OTP Verified (24h)</p>
            <CheckCircle size={16} className="text-emerald-500" />
          </div>
          <p className="font-heading text-2xl font-bold text-emerald-600">{summary.otp_verified_24h}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Flag Trend */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="flag-trend-chart">
          <h3 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider flex items-center gap-2">
            <Activity size={14} /> Security Flags (7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily_flag_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#999' }} />
              <YAxis tick={{ fontSize: 10, fill: '#999' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
              <Bar dataKey="flags" name="Flags" fill="#EC1A23" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Redemption Volume */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="redemption-trend-chart">
          <h3 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider flex items-center gap-2">
            <Activity size={14} /> Redemption Volume (7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={redemption_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#999' }} />
              <YAxis tick={{ fontSize: 10, fill: '#999' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
              <Bar dataKey="redemptions" name="Redemptions" fill="#561D1A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Flags by Severity */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="flags-by-severity">
          <h3 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">Flags by Severity</h3>
          {flags_by_severity?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={flags_by_severity} dataKey="count" nameKey="severity" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                    {flags_by_severity.map((entry, i) => (
                      <Cell key={i} fill={SEVERITY_COLORS[entry.severity]?.chart || '#999'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {flags_by_severity.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLORS[f.severity]?.bg || 'bg-gray-100'} ${SEVERITY_COLORS[f.severity]?.text || 'text-gray-600'}`}>
                      {f.severity?.charAt(0).toUpperCase() + f.severity?.slice(1)}
                    </span>
                    <span className="font-mono text-gray-600">{f.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><Shield size={24} className="mx-auto mb-2 text-gray-300" /><p>No flags</p></div>
            </div>
          )}
        </div>

        {/* Flags by Type */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="flags-by-type">
          <h3 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">Flags by Type</h3>
          {flags_by_type?.length > 0 ? (
            <div className="space-y-3">
              {flags_by_type.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-700">{TYPE_LABELS[f.type] || f.type}</span>
                      <span className="font-mono text-gray-500">{f.count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#EC1A23] rounded-full" style={{ width: `${Math.min(100, (f.count / Math.max(...flags_by_type.map(x => x.count))) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><Shield size={24} className="mx-auto mb-2 text-gray-300" /><p>No flags</p></div>
            </div>
          )}
        </div>

        {/* Ledger by Event Type */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="ledger-by-type">
          <h3 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">Ledger Events</h3>
          {ledger_by_type?.length > 0 ? (
            <div className="space-y-3">
              {ledger_by_type.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEDGER_COLORS[i % LEDGER_COLORS.length] }} />
                    <span className="text-gray-700 capitalize">{l.type}</span>
                  </div>
                  <span className="font-mono text-gray-500">{l.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><Database size={24} className="mx-auto mb-2 text-gray-300" /><p>No entries yet</p></div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Flags Timeline */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="recent-flags">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wider flex items-center gap-2">
            <ShieldAlert size={14} /> Recent Security Flags
          </h3>
          <span className="text-xs text-gray-400">{recent_flags?.length || 0} recent</span>
        </div>
        {recent_flags?.length > 0 ? (
          <div className="space-y-3">
            {recent_flags.map((flag, i) => {
              const sevStyle = SEVERITY_COLORS[flag.severity] || SEVERITY_COLORS.low;
              return (
                <div key={i} className={`rounded-xl p-4 border ${sevStyle.border} ${sevStyle.bg}`} data-testid="security-flag-row">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${sevStyle.bg} ${sevStyle.text} border ${sevStyle.border}`}>
                          {flag.severity}
                        </span>
                        <span className="text-xs font-medium text-gray-700">{TYPE_LABELS[flag.anomaly_type] || flag.anomaly_type}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 mt-1">
                        <span>Voucher: <strong className="font-mono">{flag.voucher_code}</strong></span>
                        <span>Status: <strong className={flag.status === 'open' ? 'text-red-600' : 'text-emerald-600'}>{flag.status}</strong></span>
                        <span>{formatDate(flag.created_at)}</span>
                      </div>
                      {flag.details && (
                        <div className="mt-2 text-[10px] text-gray-500">
                          {Object.entries(flag.details).filter(([k]) => k !== 'device').map(([k, v]) => (
                            <span key={k} className="inline-block mr-3">{k}: <strong>{typeof v === 'number' ? v.toLocaleString() : String(v)}</strong></span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">
            <ShieldCheck size={32} className="mx-auto mb-2 text-emerald-300" />
            <p>No security flags — all clear</p>
          </div>
        )}
      </div>
    </div>
  );
}
